"use client";

import { MS_1_MINUTE, TacticHooksProvider } from "@gettactic/hooks";
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider
} from "@tanstack/react-query";
import React, { Suspense } from "react";
import { Toaster } from "react-hot-toast";
import { I18nextProvider } from "react-i18next";
import { Progress } from "@/ui/navigation/RedirectTopProgressBar";
import { i18n } from "@/lib/lang";
import { api } from "@/lib/api/api";
import { SecondaryLayoutProviders } from "@/providers/SecondaryLayoutProviders";
import { TooltipProvider } from "@cmp-src/primitives/tooltip";
import { VercelSlugProvider } from "@/providers/auth/VercelSlugProvider";
import { usePathname } from "next/navigation";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: MS_1_MINUTE
    }
  },
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      // TODO RESTORE ERROR FUNCTION
    }
  }),
  queryCache: new QueryCache({
    onError: async (error, query) => {
      // TODO RESTORE ERROR FUNCTION
    }
  })
});

export const MainLayoutProviders: React.FC<
  React.PropsWithChildren<{ slug: string | undefined }>
> = ({ slug, children }) => {
  const pathname = usePathname();

  if (!queryClient) return null;

  const isSetupRoute = pathname?.startsWith("/setup");
  const isSignupRoute = pathname?.startsWith("/signup");
  const isKioskRoute = pathname?.startsWith("/kiosk");
  const isPublicRoute = pathname?.startsWith("/public");
  // The MCP consent page handles login itself so it can come back to the exact
  // request URL after logging in, and shows its own message to non-members
  const isMcpConnectRoute = pathname?.startsWith("/mcp/connect");

  // Kiosk routes manage their own slug via KioskProviders/KioskContext;
  // setting it here would overwrite the resolved slug on every re-render.
  if (!isKioskRoute) {
    api.updateSlug(slug ?? "");
  }

  return (
    <I18nextProvider i18n={i18n}>
      <QueryClientProvider client={queryClient}>
        <TacticHooksProvider
          key={slug ?? api.client.slug ?? "init"}
          queryClient={queryClient}
          apiClient={api}
        >
          <Suspense fallback={null}>
            <Progress />
          </Suspense>
          <TooltipProvider>
            <VercelSlugProvider api={api}>
              {isSetupRoute ||
              isSignupRoute ||
              isKioskRoute ||
              isPublicRoute ||
              isMcpConnectRoute ? (
                children
              ) : (
                <SecondaryLayoutProviders>

                    {children}

                </SecondaryLayoutProviders>
              )}
            </VercelSlugProvider>
          </TooltipProvider>
          <Toaster
            position="bottom-center"
            toastOptions={{
              duration: 4000,
              style: {
                background: "#363636",
                color: "#fff"
              },
              success: {
                duration: 3000,
                iconTheme: {
                  primary: "#4ade80",
                  secondary: "#fff"
                }
              },
              error: {
                duration: 4000,
                iconTheme: {
                  primary: "#ef4444",
                  secondary: "#fff"
                }
              }
            }}
          />
        </TacticHooksProvider>
      </QueryClientProvider>
    </I18nextProvider>
  );
};
